import React, { useCallback, useEffect, useRef, useState } from "react";
import { Instagram, Loader2, RefreshCw, Send, Sparkles, Clock, AlertTriangle, CheckCircle2, ExternalLink, Key, Trash2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

/**
 * InstagramPublisherCard — paste a public MP4 URL → auto-post to Instagram.
 *
 * Backend does a two-step Graph API dance: create container → poll status
 * → publish. This card kicks off the task and polls `/tasks/{id}` every
 * 3s so the operator sees a live progress bar without freezing the tab.
 *
 * Also holds the Golden-hour scheduler config: enable + asset URL. When
 * enabled, the backend loop publishes the asset as an IG STORY exactly
 * once at the top of every UTC hour that matches the busiest hour in the
 * last 7 days (from `presence_hourly`). Idempotent per date+hour.
 */
const POLL_MS = 3000;
const num = (v) => (typeof v === "number" ? v.toLocaleString("en-GB") : "—");

export default function InstagramPublisherCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [taskId, setTaskId] = useState(null);
  const [task, setTask] = useState(null);
  const [ghEnabled, setGhEnabled] = useState(false);
  const [ghAssetUrl, setGhAssetUrl] = useState("");
  const [ghSaving, setGhSaving] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [tokenSaving, setTokenSaving] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/instagram/status");
      setStatus(data);
      // Prime the Golden-hour form only when the admin hasn't already
      // started editing (avoids stomping on unsaved input during refresh).
      setGhEnabled((prev) => (ghSaving ? prev : Boolean(data?.golden_hour?.enabled)));
      setGhAssetUrl((prev) => (ghSaving ? prev : (data?.golden_hour?.asset_url || "")));
    } catch (e) {
      // 401/403 handled globally; other errors non-fatal
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll active task
  useEffect(() => {
    if (!taskId) return undefined;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/instagram/tasks/${taskId}`);
        setTask(data);
        if (data?.status === "done" || data?.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          loadStatus();
        }
      } catch { /* ignore transient errors */ }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [taskId, loadStatus]);

  const publish = async () => {
    const url = videoUrl.trim();
    if (!/^https?:\/\//.test(url)) { toast.error("Video URL must start with https://"); return; }
    if (!/\.(mp4|mov)(\?|$)/i.test(url)) {
      if (!window.confirm("URL doesn't look like an .mp4 — Meta needs a direct public video file. Continue anyway?")) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/instagram/publish-reel", { video_url: url, caption });
      setTaskId(data.task_id);
      setTask({ status: "queued" });
      toast.success("Publish task started — processing may take a few minutes.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveGoldenHour = async () => {
    setGhSaving(true);
    try {
      await api.post("/admin/instagram/golden-hour/config", {
        enabled: ghEnabled,
        asset_url: ghAssetUrl.trim(),
      });
      toast.success(ghEnabled ? "Golden hour ON — next run at busiest UTC hour." : "Golden hour disabled.");
      await loadStatus();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setGhSaving(false);
    }
  };

  const testTick = async () => {
    try {
      const { data } = await api.post("/admin/instagram/golden-hour/test-tick");
      toast.message("Golden-hour tick", { description: JSON.stringify(data) });
      loadStatus();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const credsOk = status?.credentials_configured;
  const igUserId = status?.ig_user_id;
  const budget = status?.budget || {};
  const gh = status?.golden_hour || {};
  const recent = status?.recent_publishes || [];

  return (
    <section
      className="border hairline rounded-2xl p-4 sm:p-5 mb-4 bg-white"
      data-testid="instagram-publisher-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="eyebrow flex items-center gap-1.5 text-[#ff4500] mb-1">
            <Instagram className="w-3 h-3" /> Instagram · auto-post
          </div>
          <h2 className="font-display text-lg sm:text-xl tracking-tight leading-tight">
            Publish a reel · schedule a Golden-hour story
          </h2>
          <p className="text-[11px] text-[#666] mt-0.5 max-w-2xl">
            Paste a public MP4 URL, add a caption, hit publish. Backend uses
            the existing Meta Page token — no separate login.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/instagram/test-connection");
                if (data?.ok) {
                  toast.success(`Connected · IG id ${String(data.ig_user_id).slice(0, 10)}…`);
                } else {
                  toast.error(`${data?.detail || "Test failed"}`, {
                    description: data?.hint || undefined, duration: 10000,
                  });
                }
                loadStatus();
              } catch (e) { toast.error(formatErr(e)); }
            }}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] mono uppercase tracking-wider border hairline hover:border-[#ff4500] hover:bg-[#fff4ee]"
            data-testid="instagram-test-connection"
            title="Read-only probe: asks Meta for the linked IG account. Posts nothing."
          >
            Test connection
          </button>
          <button
            onClick={loadStatus}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] mono uppercase tracking-wider border hairline hover:border-[#ff4500] hover:bg-[#fff4ee] disabled:opacity-50"
            data-testid="instagram-refresh"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Status strip */}
      <div className="grid grid-cols-3 gap-0 border hairline mb-3">
        <div className="p-2.5">
          <div className="mono text-[9px] uppercase tracking-[0.15em] text-[#888]">Credentials</div>
          <div className={`text-xs mono font-bold mt-1 ${credsOk ? "text-[#0a7f3f]" : "text-[#7f1d1d]"}`} data-testid="instagram-creds-status">
            {credsOk ? "SET" : "MISSING"}
          </div>
        </div>
        <div className="p-2.5 border-l hairline">
          <div className="mono text-[9px] uppercase tracking-[0.15em] text-[#888]">IG account</div>
          <div className={`text-xs mono font-bold mt-1 ${igUserId ? "text-[#0a7f3f]" : "text-[#888]"}`} data-testid="instagram-ig-user-id">
            {igUserId ? igUserId.slice(0, 14) + "…" : "—"}
          </div>
        </div>
        <div className="p-2.5 border-l hairline">
          <div className="mono text-[9px] uppercase tracking-[0.15em] text-[#888]">Budget · 24h</div>
          <div className="text-xs mono font-bold mt-1" data-testid="instagram-budget">
            {num(budget.used)} / {num(budget.cap)}
          </div>
        </div>
      </div>

      {!credsOk && (
        <div className="border hairline p-2.5 mb-3 bg-[#fff4ee] text-[#7f1d1d] text-[11px] flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <strong>No Page access token loaded.</strong> Paste one below to get started.
          </div>
        </div>
      )}

      {/* iter178 — Rotate/paste token straight from the app */}
      <div className="border hairline p-3 mb-3 bg-[#fafafa]">
        <div className="eyebrow mb-2 inline-flex items-center gap-1.5">
          <Key className="w-3 h-3" /> Page access token
        </div>
        <div className="text-[11px] text-[#666] leading-snug mb-2">
          Meta Page tokens usually expire every 60 days. Paste a fresh one here to rotate — the app saves it, probes Meta, and confirms the linked IG account in one tap. No env file, no redeploy.
          {" "}Current source: <strong className="mono uppercase text-[10px]">{status?.token_source || "—"}</strong>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Paste new Page access token here"
            className="flex-1 px-2.5 py-2 text-xs mono border hairline bg-white focus:outline-none focus:border-[#ff4500]"
            data-testid="instagram-token-input"
            autoComplete="off"
          />
          <button
            onClick={async () => {
              const tok = tokenInput.trim();
              if (tok.length < 40) { toast.error("Token looks too short — copy the full Page access token."); return; }
              setTokenSaving(true);
              try {
                const { data } = await api.post("/admin/instagram/page-token", { token: tok });
                if (data?.ok) {
                  toast.success("Token saved · verified with Meta ✓", {
                    description: data.ig_user_id
                      ? `IG account linked (id ${String(data.ig_user_id).slice(0, 10)}…). ${data.never_expires ? "Never-expires token." : data.expires_at ? "Expires " + new Date(data.expires_at).toLocaleDateString("en-GB") : ""}`
                      : `Token works BUT no IG Business account is linked to Page ${data.page_name || data.page_id}. Link it in Business Suite and hit Test connection again.`,
                    duration: 12000,
                  });
                  setTokenInput("");
                  loadStatus();
                } else {
                  toast.error("Meta rejected that token", {
                    description: data?.error || "Unknown error — check the token has instagram_content_publish scope.",
                    duration: 12000,
                  });
                }
              } catch (e) {
                toast.error(formatErr(e));
              } finally {
                setTokenSaving(false);
              }
            }}
            disabled={tokenSaving || !tokenInput.trim()}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#0e0e0e] hover:bg-[#333] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="instagram-token-save"
          >
            {tokenSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
            Save + verify
          </button>
          {status?.token_source === "override" && (
            <button
              onClick={async () => {
                if (!window.confirm("Revert to the token stored in the backend .env / Emergent Secrets?")) return;
                try {
                  await api.delete("/admin/instagram/page-token");
                  toast.success("Reverted to env token");
                  loadStatus();
                } catch (e) { toast.error(formatErr(e)); }
              }}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border hairline hover:border-[#7f1d1d] hover:bg-white text-[11px] font-bold uppercase tracking-wider text-[#7f1d1d]"
              data-testid="instagram-token-clear"
              title="Remove the app-side override — revert to the env var"
            >
              <Trash2 className="w-3 h-3" /> Clear override
            </button>
          )}
        </div>
      </div>

      {/* Publish reel form */}
      <div className="border hairline p-3 mb-3 bg-[#fafafa]">
        <div className="eyebrow mb-2 inline-flex items-center gap-1.5">
          <Send className="w-3 h-3" /> Publish a reel now
        </div>
        <label className="block mb-2">
          <span className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1 block">
            Public MP4 URL (direct file, not a page)
          </span>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://your-cdn.example.com/reel.mp4"
            className="w-full px-2.5 py-2 text-xs mono border hairline bg-white focus:outline-none focus:border-[#ff4500]"
            data-testid="instagram-publish-url"
          />
        </label>
        <label className="block mb-2">
          <span className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1 block">
            Caption (max 2200 chars)
          </span>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={2200}
            rows={3}
            placeholder="Hidden gem in Cornwall — sea views, dog-friendly, and £14/night. Save this one 👇"
            className="w-full px-2.5 py-2 text-xs border hairline bg-white focus:outline-none focus:border-[#ff4500]"
            data-testid="instagram-publish-caption"
          />
          <div className="text-right mono text-[9px] text-[#888] mt-0.5">{caption.length}/2200</div>
        </label>
        <button
          onClick={publish}
          disabled={busy || !credsOk || !videoUrl.trim() || (budget.remaining === 0)}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs font-bold uppercase tracking-wider shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="instagram-publish-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Instagram className="w-3.5 h-3.5" />}
          Publish reel
        </button>
      </div>

      {/* Active task progress */}
      {task && (
        <div className="border hairline p-3 mb-3 bg-white" data-testid="instagram-task-status">
          <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">Active task · {taskId}</div>
          {task.status === "done" ? (
            <div className="flex items-start gap-2 text-[#065f46] text-xs">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <div>
                <div className="font-bold">Published ✓</div>
                <div className="mono text-[10px] text-[#0e0e0e]">media_id: {task.media_id}</div>
                {task.public_url && (
                  <a href={task.public_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[#ff4500] underline mt-1 text-[10px] mono uppercase tracking-wider"
                    data-testid="instagram-task-open">
                    Open on Instagram <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ) : task.status === "error" ? (
            <div className="flex items-start gap-2 text-[#7f1d1d] text-xs">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <div>
                <div className="font-bold">Failed</div>
                <div className="mono text-[10px]">{task.error || "Unknown error"}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-wider text-[#666]">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-[#ff4500]" />
              {task.status || "queued"}
              {task.container_status && ` · ${task.container_status}`}
            </div>
          )}
        </div>
      )}

      {/* Golden hour scheduler */}
      <div className="border hairline p-3 mb-3 bg-[#fafafa]">
        <div className="eyebrow mb-2 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Golden-hour auto-story
        </div>
        <div className="text-[11px] text-[#666] leading-snug mb-2">
          Auto-publishes the asset below as an Instagram <strong>Story</strong> at the top of every UTC hour that matches your busiest hour (last 7 days).
          One post per date+hour, idempotent across pod restarts.
        </div>
        <div className="flex items-center gap-4 flex-wrap mono text-[10px] uppercase tracking-wider text-[#666] mb-2">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> busiest hour:
            <span className="text-[#ff4500] font-bold">
              {gh.busiest_hour_utc !== null && gh.busiest_hour_utc !== undefined
                ? String(gh.busiest_hour_utc).padStart(2, "0") + ":00"
                : "—"}
            </span>
          </span>
          <span>current UTC hour: <span className="text-[#0e0e0e] font-bold">{String(gh.current_hour_utc ?? "").padStart(2, "0")}:00</span></span>
        </div>
        <label className="inline-flex items-center gap-2 mb-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={ghEnabled}
            onChange={(e) => setGhEnabled(e.target.checked)}
            data-testid="instagram-golden-hour-enabled"
          />
          Enable Golden-hour auto-story
        </label>
        <label className="block mb-2">
          <span className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1 block">
            Story asset URL (public MP4, 9:16 recommended)
          </span>
          <input
            type="url"
            value={ghAssetUrl}
            onChange={(e) => setGhAssetUrl(e.target.value)}
            placeholder="https://your-cdn.example.com/golden-hour-story.mp4"
            className="w-full px-2.5 py-2 text-xs mono border hairline bg-white focus:outline-none focus:border-[#ff4500]"
            data-testid="instagram-golden-hour-url"
          />
        </label>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={saveGoldenHour}
            disabled={ghSaving}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#0e0e0e] hover:bg-[#333] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="instagram-golden-hour-save"
          >
            {ghSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Save config
          </button>
          <button
            onClick={testTick}
            disabled={!credsOk}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border hairline hover:border-[#ff4500] hover:bg-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="instagram-golden-hour-test"
            title="Manually run the Golden-hour scheduler now (respects idempotency)"
          >
            Test tick
          </button>
        </div>
      </div>

      {/* Recent posts */}
      {recent.length > 0 && (
        <div className="border hairline p-3" data-testid="instagram-recent-list">
          <div className="eyebrow mb-2">Recent publishes</div>
          <div className="space-y-1.5">
            {recent.map((p, i) => (
              <div key={p.task_id || i} className="flex items-start justify-between gap-2 text-[11px] mono border-b hairline last:border-b-0 py-1.5">
                <div className="min-w-0 flex-1">
                  <span className="uppercase text-[9px] tracking-wider mr-1 text-[#888]">{p.media_type || "—"}</span>
                  <span className="text-[9px] tracking-wider mr-1 text-[#0a7f3f]">{p.source || "manual"}</span>
                  <span className="text-[#333]">{p.published_at ? new Date(p.published_at).toLocaleString("en-GB") : "—"}</span>
                  {p.media_id && <div className="text-[9px] text-[#666] truncate">media_id: {p.media_id}</div>}
                </div>
                {p.media_id && (
                  <a
                    href={p.media_type === "REELS" ? `https://www.instagram.com/reel/${p.media_id}/` : `https://www.instagram.com/p/${p.media_id}/`}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-[#ff4500] underline text-[9px] uppercase tracking-wider inline-flex items-center gap-0.5"
                  >
                    Open <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
